/**
 * Pathways v3, the learner lane's transport: every call of
 * docs/pathways-module/pathways-api-contract.md §5.2 (public), §5.3 (checkout), §5.4
 * (learner) and §10.3 (the practice submission). The practitioner calls live in ./adminApi.js.
 *
 * Paths are relative to the api baseURL ("/api"), like every other caller of `api`. Ids and
 * tokens travel through `encodeURIComponent`.
 *
 * SHAPE-GUARDED. The production proxy answers 200 + index.html for an unknown `/api` path,
 * so a 200 whose body is not the shape the contract promises is refused with
 * `payloadShapeError` rather than rendered as "no pathways" or an empty step. An HTTP error
 * is re-thrown untouched, so callers can read its status and its `detail.code`
 * (pathways/errors.js).
 *
 * Bodies are sent exactly as the contract names them: every write model is extra="forbid"
 * on the server, so nothing is added here that the caller did not pass.
 *
 * "@/lib/api", not a relative path: axios cannot load under Jest, and every suite that
 * mounts a pathway surface stubs this exact specifier.
 */
import { api, getCurrentTenantSlug } from "@/lib/api";
// Relative: pure modules, resolvable under Jest without a shim.
import { payloadShapeError } from "../hooks/useAdminLoad";
import { isTenantHost } from "../lib/tenantHost";

const enc = encodeURIComponent;

// ── shape guards ─────────────────────────────────────────────────────────────

const isObject = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const isStr = (v) => typeof v === "string";
const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const isBool = (v) => typeof v === "boolean";
const isArrayOf = (v, pred) => Array.isArray(v) && v.every(pred);
const hasIdTitle = (v) => isObject(v) && isStr(v.id) && isStr(v.title);
const isNullOr = (v, pred) => v === null || pred(v);

const isOutlineStep = (s) => hasIdTitle(s) && isStr(s.kind) && isBool(s.required);
const isOutlineLevel = (l) => hasIdTitle(l) && isArrayOf(l.steps, isOutlineStep);

const isPublicCard = (v) => hasIdTitle(v) && isStr(v.path_type)
  && isArrayOf(v.objectives, isStr) && isNum(v.levels_count) && isNum(v.steps_count)
  && isNum(v.estimated_minutes) && isNum(v.price_cents)
  && isNullOr(v.spots_left, isNum);
const isPublicDetail = (v) => isPublicCard(v) && isBool(v.viewer_enrolled)
  && isArrayOf(v.levels, isOutlineLevel);
const isEnrollResult = (v) => isObject(v) && v.ok === true && isBool(v.already_enrolled);
const isUnsubscribeResult = (v) => isObject(v) && v.ok === true && isStr(v.pathway_title);
const isCheckoutResult = (v) => isObject(v)
  && ((isStr(v.checkout_url) && v.checkout_url !== "") || v.enrolled === true);
const isCheckoutStatus = (v) => isObject(v) && isStr(v.status);

const isCurrentStep = (v) => hasIdTitle(v) && isStr(v.kind);
const isMemberSummary = (v) => hasIdTitle(v) && isStr(v.path_type) && isNum(v.percent)
  && isNum(v.required_total) && isNum(v.required_completed)
  && isNullOr(v.current_step, isCurrentStep);
const isStepRow = (v) => hasIdTitle(v) && isStr(v.kind) && isStr(v.state)
  && isBool(v.required) && isBool(v.current);
const isHomeLevel = (l) => hasIdTitle(l) && isNum(l.index) && isArrayOf(l.steps, isStepRow);
const isMemberHome = (v) => isMemberSummary(v) && isArrayOf(v.objectives, isStr)
  && isArrayOf(v.levels, isHomeLevel) && Array.isArray(v.prompts)
  && isNullOr(v.certificate, (c) => isObject(c) && isBool(c.available));
// Phase 3 (contract §10.7). Each is optional on the wire, so a screen of a v3 kind that
// does not carry the key at all still passes; when present it must have its shape.
const isSubmission = (v) => isObject(v) && isStr(v.id) && isStr(v.status)
  && isNullOr(v.photo_url, isStr);
const isPracticeScreen = (v) => isObject(v) && isBool(v.submission_required)
  && isArrayOf(v.submissions, isSubmission);
const isLiveScreen = (v) => isObject(v) && (v.mode === "cohort" || v.mode === "self_paced");
const isCourseScreen = (v) => hasIdTitle(v) && isNum(v.lessons_total) && isNum(v.lessons_done)
  && isBool(v.published) && isStr(v.player_path);
const optional = (v, pred) => v === undefined || isNullOr(v, pred);
const isStepScreen = (v) => isObject(v) && isStr(v.pathway_id) && isStr(v.path_type)
  && hasIdTitle(v.level) && isObject(v.position) && isNum(v.position.index)
  && isStepRow(v.step) && Array.isArray(v.resources)
  && isNullOr(v.next, hasIdTitle)
  && optional(v.practice, isPracticeScreen) && optional(v.live, isLiveScreen)
  && optional(v.course, isCourseScreen);
const isCompleteResult = (v) => isObject(v) && isBool(v.already_completed)
  && isStepRow(v.step) && isNum(v.percent) && isBool(v.pathway_completed);
const isSubmissionResult = (v) => isCompleteResult(v) && isSubmission(v.submission);
const isQuizResult = (v) => isObject(v) && isNum(v.score) && isBool(v.passed)
  && isNum(v.threshold) && isNum(v.attempts) && Array.isArray(v.detail) && isStepRow(v.step);

/** Returns `data` when `ok(data)`, otherwise throws the shape refusal. */
const guard = (data, ok) => {
  if (!ok(data)) throw payloadShapeError();
  return data;
};

// ── API-relative URLs (contract §5.4) ────────────────────────────────────────

/**
 * An address the BROWSER itself requests (a `<video src>`, an `<a href>`), resolved against
 * the API the same way axios would reach it.
 *
 * WHY NOT `baseURL + path`. On a platform host axios names the practice in an
 * `X-Tenant-Slug` header (lib/api.js's interceptor), and a navigation or a media element
 * cannot send a header. The middleware's other documented door is the `/api/t/{slug}/`
 * path prefix (backend/tenant_middleware.py), so the slug goes into the path exactly when
 * the interceptor would have sent the header. On a tenant host the Host resolves the
 * practice, and the prefix is absent just as the header is. (gateway/api.js's departure
 * beacon applies the same rule for the same reason.) The session cookie rides along on
 * its own: `withCredentials` is what axios does, and a same-site navigation does it too.
 *
 * Three input forms:
 *   "https://…"         an absolute URL (a link video's embed URL): returned unchanged
 *   "/api/…"            an absolute API path (the admin preview's `/api/media/file/<id>`,
 *                       contract §9.1): its "/api" is the base's own, so it is read as
 *                       the API-relative path after it
 *   "/client/…"         an API-relative path (every url of a member step screen)
 *
 * @param {string} url
 * @returns {string} "" for an empty input
 */
export function resolveApiUrl(url) {
  const raw = String(url || "");
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const relative = raw.startsWith("/api/") ? raw.slice(4) : raw;
  const path = relative.startsWith("/") ? relative : `/${relative}`;
  const slug = isTenantHost() ? null : getCurrentTenantSlug();
  const base = String(api?.defaults?.baseURL || "").replace(/\/+$/, "");
  return `${base}${slug ? `/t/${enc(slug)}` : ""}${path}`;
}

// ── public (§5.2) ────────────────────────────────────────────────────────────

/** `GET /public/pathways` -> PublicPathwayCard[]. No session needed. */
export async function fetchPublicPathways() {
  const { data } = await api.get("/public/pathways");
  return guard(data, (d) => isArrayOf(d, isPublicCard));
}

/** `GET /public/pathways/{id}` -> PublicPathwayDetail. 404 for absent, draft or archived. */
export async function fetchPublicPathway(id) {
  const { data } = await api.get(`/public/pathways/${enc(id)}`);
  return guard(data, isPublicDetail);
}

/**
 * `POST /public/pathways/{id}/enroll` -> `{ ok, already_enrolled, pathway_id, spots_left }`.
 * Free pathways only (a paid one answers 409 PAYMENT_REQUIRED). The learner is the session;
 * the body carries no address.
 *
 * @param {string} id
 * @param {{ name: string, locale: "fr"|"en"|"es" }} body
 */
export async function enrollInPathway(id, { name, locale }) {
  const { data } = await api.post(`/public/pathways/${enc(id)}/enroll`, { name, locale });
  return guard(data, isEnrollResult);
}

/** `POST /public/pathways/nudges/unsubscribe` -> `{ ok, pathway_title }`; 400 TOKEN_INVALID. */
export async function unsubscribeNudges(token) {
  const { data } = await api.post("/public/pathways/nudges/unsubscribe", { token });
  return guard(data, isUnsubscribeResult);
}

// ── checkout (§5.3) ──────────────────────────────────────────────────────────

/**
 * `POST /checkout/connect/pathway` -> `{ checkout_url, session_id }`, or
 * `{ enrolled: true, pathway_id }` when a gift card or a promo covered the whole price.
 * The body is built by learnerModel.checkoutBody; `ref` is present only when non-empty.
 *
 * @param {{ pathway_id: string, name: string, locale: string, origin_url: string,
 *           return_path: string, ref?: string }} body
 */
export async function openPathwayCheckout(body) {
  const { data } = await api.post("/checkout/connect/pathway", body);
  return guard(data, isCheckoutResult);
}

/** `GET /checkout/connect/status/{session_id}` -> `{ status, … }` ("paid" once captured). */
export async function fetchCheckoutStatus(sessionId) {
  const { data } = await api.get(`/checkout/connect/status/${enc(sessionId)}`);
  return guard(data, isCheckoutStatus);
}

// ── learner (§5.4, client session required) ──────────────────────────────────

const MEMBER = "/client/pathways";

/** `GET /client/pathways` -> MemberPathwaySummary[] (enrolled_at desc). */
export async function fetchMemberPathways() {
  const { data } = await api.get(MEMBER);
  return guard(data, (d) => isArrayOf(d, isMemberSummary));
}

/** `GET /client/pathways/{id}` -> MemberPathwayHome. 404 when absent, draft or not joined. */
export async function fetchMemberPathway(id) {
  const { data } = await api.get(`${MEMBER}/${enc(id)}`);
  return guard(data, isMemberHome);
}

/** `GET /client/pathways/{id}/steps/{sid}` -> MemberStepScreen. 409 STEP_LOCKED. */
export async function fetchStepScreen(id, stepId) {
  const { data } = await api.get(`${MEMBER}/${enc(id)}/steps/${enc(stepId)}`);
  return guard(data, isStepScreen);
}

/**
 * `POST /client/pathways/{id}/steps/{sid}/complete` with `{}` -> StepCompleteResult.
 * Idempotent: a repeat answers `already_completed: true`.
 */
export async function completeStep(id, stepId) {
  const { data } = await api.post(`${MEMBER}/${enc(id)}/steps/${enc(stepId)}/complete`, {});
  return guard(data, isCompleteResult);
}

/**
 * `POST /client/pathways/{id}/steps/{sid}/quiz-submit` -> QuizSubmitResult.
 *
 * @param {string} id
 * @param {string} stepId
 * @param {number[]} answers one choice index per question, in order
 */
export async function submitQuiz(id, stepId, answers) {
  const { data } = await api.post(`${MEMBER}/${enc(id)}/steps/${enc(stepId)}/quiz-submit`, { answers });
  return guard(data, isQuizResult);
}

/**
 * `POST /client/pathways/{id}/steps/{sid}/submission`, multipart -> SubmissionResult
 * (contract §10.3): a practice step's text and/or photo. It completes the step; a
 * resubmission answers `already_completed: true` and goes back into the review queue.
 *
 * `text` is always sent (0..5000, the server trims it); `photo` only when one was picked,
 * already checked against learnerModel.photoProblem.
 *
 * @param {string} id
 * @param {string} stepId
 * @param {{ text: string, photo: File|null }} body
 */
export async function submitPractice(id, stepId, { text, photo }) {
  const form = new FormData();
  form.append("text", text);
  if (photo) form.append("photo", photo, photo.name);
  const { data } = await api.post(`${MEMBER}/${enc(id)}/steps/${enc(stepId)}/submission`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return guard(data, isSubmissionResult);
}

/** The certificate PDF's address (`GET /client/pathways/{id}/certificate.pdf`), for an `<a href>`. */
export const certificateUrl = (id) => resolveApiUrl(`${MEMBER}/${enc(id)}/certificate.pdf`);
